import React, { useState, useEffect, useMemo } from 'react';
import { Euro, ArrowUp, ArrowDown, ArrowUpDown, Package, Loader2 } from 'lucide-react';
import api from '../../../../services/api';

interface BpuDetailedRow {
  consumable_id: number;
  gamme: string;
  product_name: string;
  unit: string;
  batch_size: number;
  prix_catalogue_ht: number;
  prix_vente_conseille_ttc: number | null;
  tier_number: number | null;
  qty_lots: number | null;
  qty_units: number | null;
  remise_percent: number;
  free_units: number;
  remise_display: string;
  prix_client_ht: number;
  tva_rate: number;
  discount_mode: string | null;
  gamme_config_id: number | null;
  tier_source?: string | null;
}

interface BpuDetailedResponse {
  client_id: number;
  client_name: string;
  marque: string | null;
  tva_rate: number;
  rows: BpuDetailedRow[];
  has_active_bpu?: boolean;
  active_bpu_id?: number | null;
  active_bpu_reference_number?: string | null;
  bpu_origin?: string | null;
  bpu_origin_label?: string | null;
}

interface BpuDetailedViewProps {
  clientId?: number;
  useMyEndpoint?: boolean;
}

type SortKey = 'gamme' | 'product_name' | 'unit' | 'tier_number' | 'prix_catalogue_ht' | 'prix_client_ht';

interface ProductRowGroup {
  readonly groupKey: string;
  readonly rows: BpuDetailedRow[];
}

type ProductGroups = Record<string, BpuDetailedRow[]>;
type GammeGroups = Record<string, ProductRowGroup[]>;
type SortableValue = string | number | null;

export const BpuDetailedView: React.FC<BpuDetailedViewProps> = ({ clientId, useMyEndpoint }) => {
  const [data, setData] = useState<BpuDetailedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'gamme',
    direction: 'asc'
  });

  const isClientPortal = typeof window !== 'undefined' && window.location.pathname.startsWith('/client');
  const shouldUseMyEndpoint = Boolean(useMyEndpoint || isClientPortal);

  const extractErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      const response = (err as { response?: { data?: { detail?: string } } }).response;
      return response?.data?.detail || 'Erreur lors du chargement des données BPU';
    }
    if (err instanceof Error) {
      return err.message;
    }
    return 'Erreur lors du chargement des données BPU';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const endpoint = shouldUseMyEndpoint
          ? '/clients/me/bpu-detailed'
          : `/clients/${clientId}/bpu-detailed`;
        const response = await api.get(endpoint);
        setData(response.data);
      } catch (err: unknown) {
        console.error('Error fetching BPU detailed data:', err);
        setError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    if (shouldUseMyEndpoint || clientId) {
      fetchData();
    }
  }, [clientId, shouldUseMyEndpoint]);

  const requestSort = (key: SortKey) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const buildRowSignature = (row: BpuDetailedRow): string => {
    return [
      row.gamme,
      row.unit,
      row.batch_size,
      row.prix_catalogue_ht,
      row.tier_number ?? 'none',
      row.qty_lots ?? 'none',
      row.qty_units ?? 'none',
      row.remise_display,
      row.prix_client_ht
    ].join('|');
  };

  const uniqueRows = useMemo(() => {
    if (!data?.rows) return [];

    const seenSignatures: Set<string> = new Set<string>();
    return data.rows.filter((row) => {
      const signature: string = buildRowSignature(row);
      if (seenSignatures.has(signature)) {
        return false;
      }
      seenSignatures.add(signature);
      return true;
    });
  }, [data?.rows]);

  const sortedRows = useMemo(() => {
    if (!uniqueRows.length) return [];
    
    return [...uniqueRows].sort((a, b) => {
      let aValue: SortableValue = a[sortConfig.key];
      let bValue: SortableValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (typeof aValue === 'string' || typeof bValue === 'string') {
        const normalizedAValue: string = String(aValue);
        const normalizedBValue: string = String(bValue);
        return sortConfig.direction === 'asc'
          ? normalizedAValue.localeCompare(normalizedBValue)
          : normalizedBValue.localeCompare(normalizedAValue);
      }

      const normalizedAValue: number = typeof aValue === 'number' ? aValue : Number(aValue);
      const normalizedBValue: number = typeof bValue === 'number' ? bValue : Number(bValue);

      return sortConfig.direction === 'asc'
        ? normalizedAValue - normalizedBValue
        : normalizedBValue - normalizedAValue;
    });
  }, [uniqueRows, sortConfig]);

  const groupedByProduct = useMemo(() => {
    const groups: ProductGroups = {};
    sortedRows.forEach(row => {
      const key: string = `${row.gamme}|${row.unit}|${row.batch_size}|${row.prix_catalogue_ht}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(row);
    });
    return groups;
  }, [sortedRows]);

  const groupedByGamme = useMemo(() => {
    const groups: GammeGroups = {};

    Object.entries(groupedByProduct).forEach(([groupKey, rows]) => {
      const gammeKey: string = rows[0]?.gamme || 'Sans gamme';

      if (!groups[gammeKey]) {
        groups[gammeKey] = [];
      }

      groups[gammeKey].push({
        groupKey,
        rows
      });
    });

    return groups;
  }, [groupedByProduct]);

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3" /> 
      : <ArrowDown className="w-3 h-3" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
        <span className="ml-3 text-gray-500">Chargement des données BPU...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Euro className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <div className="text-center py-12">
        <Euro className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Aucune donnée BPU disponible</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg">
      {/* Header avec infos marque et TVA */}
      <div className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-600 dark:text-gray-400">Marque:</span>
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">
              {data.marque || 'Toutes marques'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-600 dark:text-gray-400">Mode Remise:</span>
            <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded text-xs font-medium">
              %
            </span>
          </div>
          {data.bpu_origin_label && (
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-600 dark:text-gray-400">Origine BPU:</span>
              <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-xs font-medium">
                {data.bpu_origin_label}
              </span>
            </div>
          )}
          {data.active_bpu_reference_number && (
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-600 dark:text-gray-400">Référence:</span>
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {data.active_bpu_reference_number}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: card list (< md) */}
      <div className="md:hidden space-y-3">
        {Object.entries(groupedByGamme).map(([gammeKey, productGroups]) => {
          return (
            <div
              key={gammeKey}
              className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden"
            >
              <div className="px-3 py-2.5 bg-gradient-to-r from-gray-50 to-emerald-50/50 dark:from-slate-800 dark:to-emerald-900/10 border-b border-gray-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="font-semibold text-sm text-gray-900 dark:text-slate-100 truncate">{gammeKey}</span>
                </div>
              </div>

              <div className="divide-y divide-gray-100 dark:divide-slate-800">
                {productGroups.map(({ groupKey, rows }) => {
                  const firstRow: BpuDetailedRow = rows[0];

                  return (
                    <div key={groupKey} className="px-3 py-2">
                      <div className="mb-2 text-[11px] text-gray-500 dark:text-slate-400">
                        <span>{firstRow.unit} · Lot de {firstRow.batch_size}</span>
                        <span className="ml-2">Catalogue: <span className="font-semibold text-gray-700 dark:text-slate-300">{firstRow.prix_catalogue_ht.toFixed(2)} €HT</span></span>
                        <span className="ml-2">TVA: {firstRow.tva_rate}%</span>
                      </div>
                      <div className="space-y-2">
                        {rows.map((row: BpuDetailedRow) => (
                          <div
                            key={`${groupKey}-${row.tier_number ?? 'base'}-${row.qty_units ?? 'none'}-${row.prix_client_ht}`}
                            className="flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {row.tier_number !== null ? (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[10px] font-bold flex-shrink-0">
                                  {row.tier_number}
                                </span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-400 text-[10px] flex-shrink-0">—</span>
                              )}
                              <div className="text-xs text-gray-600 dark:text-slate-400">
                                {row.qty_lots !== null && <span>{row.qty_lots} lot{(row.qty_lots ?? 0) > 1 ? 's' : ''}</span>}
                                {row.qty_units !== null && <span className="ml-1 text-gray-400">({row.qty_units} un)</span>}
                              </div>
                              {row.remise_display !== '-' && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  row.discount_mode === 'free_units'
                                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                                    : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                                }`}>
                                  {row.remise_display}
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400 flex-shrink-0">
                              {row.prix_client_ht.toFixed(2)} €HT
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: table (md+) */}
      <div className="hidden md:block overflow-x-auto border border-gray-200 dark:border-slate-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
          <thead className="bg-gray-100 dark:bg-slate-800">
            <tr>
              <th
                className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors group"
                onClick={() => requestSort('gamme')}
              >
                <div className="flex items-center gap-1">
                  Gamme
                  <SortIcon columnKey="gamme" />
                </div>
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
                Unité (Un)
              </th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
                Lot (Un)
              </th>
              <th
                className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors group"
                onClick={() => requestSort('prix_catalogue_ht')}
              >
                <div className="flex items-center justify-center gap-1">
                  Prix Catalogue €HT
                  <SortIcon columnKey="prix_catalogue_ht" />
                </div>
              </th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
                Paliers
              </th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
                Qté (Lots)
              </th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
                Qté (Un)
              </th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
                Remise
              </th>
              <th
                className="px-3 py-3 text-center text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors group"
                onClick={() => requestSort('prix_client_ht')}
              >
                <div className="flex items-center justify-center gap-1">
                  Prix Client €HT
                  <SortIcon columnKey="prix_client_ht" />
                </div>
              </th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">
                TVA
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
            {Object.entries(groupedByGamme).map(([gammeKey, productGroups]) => {
              const gammeRowSpan: number = productGroups.reduce((total: number, productGroup: ProductRowGroup) => {
                return total + productGroup.rows.length;
              }, 0);

              return productGroups.flatMap(({ groupKey, rows }, productGroupIndex: number) => {
                return rows.map((row: BpuDetailedRow, rowIndex: number) => {
                  const isFirstProductRow: boolean = rowIndex === 0;
                  const isFirstGammeRow: boolean = productGroupIndex === 0 && rowIndex === 0;
                  const rowSpan: number = rows.length;

                  return (
                    <tr
                      key={`${groupKey}-${row.tier_number ?? 'base'}-${row.qty_units ?? 'none'}-${row.prix_client_ht}`}
                      className={`hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors ${
                        isFirstProductRow ? 'border-t-2 border-gray-200 dark:border-slate-600' : ''
                      }`}
                    >
                      {isFirstGammeRow && (
                        <td
                          rowSpan={gammeRowSpan}
                          className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-slate-100 bg-gray-50/50 dark:bg-slate-800/30 border-r border-gray-100 dark:border-slate-700 align-top"
                        >
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-emerald-500" />
                            <span className="line-clamp-2">{gammeKey || 'Sans gamme'}</span>
                          </div>
                        </td>
                      )}
                      {isFirstProductRow && (
                        <td
                          rowSpan={rowSpan}
                          className="px-3 py-2 text-sm text-gray-600 dark:text-slate-400 bg-gray-50/50 dark:bg-slate-800/30 border-r border-gray-100 dark:border-slate-700"
                        >
                          {row.unit}
                        </td>
                      )}
                      {isFirstProductRow && (
                        <td
                          rowSpan={rowSpan}
                          className="px-3 py-2 text-sm text-center text-gray-600 dark:text-slate-400 bg-gray-50/50 dark:bg-slate-800/30 border-r border-gray-100 dark:border-slate-700"
                        >
                          {row.batch_size}
                        </td>
                      )}
                      {isFirstProductRow && (
                        <td
                          rowSpan={rowSpan}
                          className="px-3 py-2 text-sm text-center text-gray-700 dark:text-slate-300 bg-gray-50/50 dark:bg-slate-800/30 border-r border-gray-100 dark:border-slate-700 font-medium"
                        >
                          {row.prix_catalogue_ht.toFixed(2)} €HT
                        </td>
                      )}
                      <td className="px-3 py-2 text-sm text-center">
                        {row.tier_number !== null ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-semibold">
                            {row.tier_number}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-center text-gray-700 dark:text-slate-300">
                        {row.qty_lots !== null ? row.qty_lots : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-center text-gray-700 dark:text-slate-300 font-medium">
                        {row.qty_units !== null ? row.qty_units : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-center">
                        {row.remise_display !== '-' ? (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            row.discount_mode === 'free_units'
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                              : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                          }`}>
                            {row.remise_display}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-center font-semibold text-emerald-700 dark:text-emerald-400">
                        {row.prix_client_ht.toFixed(2)} €HT
                      </td>
                      <td className="px-3 py-2 text-sm text-center text-gray-500 dark:text-slate-400">
                        {row.tva_rate}%
                      </td>
                    </tr>
                  );
                });
              });
            })}
          </tbody>
        </table>
      </div>

      {/* Footer avec légende */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-lg text-xs text-gray-500 dark:text-slate-400">
        <div className="flex flex-wrap gap-4">

       
          <div className="ml-auto text-gray-400">
            Prix Client €HT = Prix Catalogue €HT × (1 - Remise)
          </div>
        </div>
      </div>
    </div>
  );
};

export default BpuDetailedView;
